import * as React from 'react';
import { Box, Button, IconButton, Typography } from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import AddIcon from '@material-ui/icons/Add';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import FormatListBulletedIcon from '@material-ui/icons/FormatListBulleted';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { useTaskflowBuilder } from '../taskflowBuilderContext';
import { TASKFLOW_LANE_LABEL, TaskflowTask } from '../types';
import { createBlankTask } from '../seedData';
import { ConfigureTaskDialog } from '../configureTaskDialog/ConfigureTaskDialog';
import { DiagramPreviewDialog } from '../dialogs/DiagramPreviewDialog';
import { DeleteConfirmationDialog } from '../../components/Dialogs/DeleteConfirmationDialog';
import { SectionHeading } from '../../components/cards/SectionHeading';
import VisibilityIcon from '@material-ui/icons/Visibility';

const LANE_COLOR: Record<TaskflowTask['lane'], string> = {
    records: '#1565c0',
    fee: '#2e7d32',
    sec: '#e18713',
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
        },
        head: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: theme.spacing(2, 2.75),
            borderBottom: `1px solid ${theme.palette.divider}`,
            flexShrink: 0,
        },
        headActions: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
        },
        table: {
            padding: theme.spacing(0.75, 1.75, 2, 1.75),
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
        },
        taskListScroll: {
            flex: 1,
            minHeight: 0,
            paddingRight: theme.spacing(0.75),
            marginRight: theme.spacing(-0.75),
            position: 'relative',
        },
        taskListContainer: {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflowY: 'auto',
        },
        headerRow: {
            display: 'grid',
            gridTemplateColumns: '22px 26px 1.6fr 1fr 0.8fr 1.1fr auto',
            gap: theme.spacing(1.5),
            padding: theme.spacing(1, 1.25),
            fontSize: 10.5,
            fontWeight: 700,
            color: '#6b7280',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            flexShrink: 0,
        },
        row: {
            display: 'grid',
            gridTemplateColumns: '22px 26px 1.6fr 1fr 0.8fr 1.1fr auto',
            gap: theme.spacing(1.5),
            alignItems: 'center',
            padding: theme.spacing(1.25, 1.25),
            border: '1px solid #f3f4f6',
            borderRadius: 10,
            marginTop: theme.spacing(0.75),
            backgroundColor: theme.palette.background.paper,
        },
        rowDragging: {
            boxShadow: '0 4px 18px rgba(0,0,0,0.14)',
        },
        grip: {
            color: theme.palette.text.disabled,
            display: 'flex',
        },
        stepNumber: {
            width: 22,
            height: 22,
            borderRadius: '50%',
            backgroundColor: theme.palette.text.primary,
            color: theme.palette.background.paper,
            fontSize: 10.5,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        stepNumberGate: {
            backgroundColor: '#fff6e9',
            color: '#a15f04',
            border: '1.5px solid #e18713',
        },
        desc: {
            fontSize: 13,
            fontWeight: 700,
        },
        resp: {
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12,
            color: '#6b7280',
        },
        laneDot: {
            width: 7,
            height: 7,
            borderRadius: '50%',
            flexShrink: 0,
        },
        priority: {
            fontSize: 11,
            color: '#6b7280',
        },
        due: {
            fontSize: 11.5,
            color: '#6b7280',
        },
        rowActions: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(0.5),
        },
        editIconBtn: {
            color: theme.palette.action.active,
            '&:hover': {
                color: theme.palette.primary.main,
                backgroundColor: 'rgba(21, 101, 192, 0.08)',
            },
        },
        deleteIconBtn: {
            color: theme.palette.action.active,
            '&:hover': {
                color: theme.palette.error.main,
                backgroundColor: 'rgba(211, 47, 47, 0.08)',
            },
        },
    })
);

const describeDueDate = (task: TaskflowTask): string => {
    if (task.due.manual) {
        return 'Manual';
    }
    const sign = task.due.mode === 'add' ? '+' : '−';
    const unit = task.due.basis === 'work' ? ' working day' + (task.due.days === '1' ? '' : 's') : 'd';
    if (task.due.basis === 'work') {
        return `${sign}${task.due.days || '0'}${unit}`;
    }
    return task.due.days === '0' ? 'Same day' : `${sign}${task.due.days}${unit}`;
};

export const TaskListStep: React.FC = () => {
    const classes = useStyles();
    const { activeTaskflow, dispatch } = useTaskflowBuilder();
    const [isDiagramPreviewOpen, setIsDiagramPreviewOpen] = React.useState(false);
    const [isConfigureTaskDialogOpen, setIsConfigureTaskDialogOpen] = React.useState(false);
    const [draftTask, setDraftTask] = React.useState<TaskflowTask | null>(null);
    const [taskPendingDelete, setTaskPendingDelete] = React.useState<TaskflowTask | null>(null);

    if (!activeTaskflow) {
        return null;
    }

    const openTaskEditor = (taskId: string) => () => {
        dispatch({ type: 'SELECT_TASK', taskId });
        setDraftTask(null);
        setIsConfigureTaskDialogOpen(true);
    };

    const addTask = () => {
        setDraftTask(createBlankTask(`task-${Date.now()}`));
        setIsConfigureTaskDialogOpen(true);
    };

    const closeConfigureTaskDialog = () => {
        setIsConfigureTaskDialogOpen(false);
        setDraftTask(null);
    };

    const requestDeleteTask = (task: TaskflowTask) => () => {
        setTaskPendingDelete(task);
    };

    const closeDeleteConfirmation = () => {
        setTaskPendingDelete(null);
    };

    const confirmDeleteTask = () => {
        if (taskPendingDelete) {
            dispatch({ type: 'REMOVE_TASK', taskId: taskPendingDelete.id });
        }
        setTaskPendingDelete(null);
    };

    const onDragEnd = (result: DropResult) => {
        if (!result.destination) {
            return;
        }
        dispatch({ type: 'REORDER_TASKS', sourceIndex: result.source.index, destinationIndex: result.destination.index });
    };

    return (
        <Box className={classes.root}>
            <Box className={classes.head}>
                <SectionHeading
                    title={activeTaskflow.name || 'Untitled taskflow'}
                    color="#2e7d32"
                    bgColor="#e8f5e9"
                    icon={FormatListBulletedIcon}
                    headerAction={
                        <Box className={classes.headActions}>
                            <Button variant="contained" color="primary" size="small" startIcon={<AddIcon fontSize="small" />} onClick={addTask}>
                                Add task
                            </Button>
                            <Button 
                                variant="outlined" 
                                size="small" 
                                onClick={() => setIsDiagramPreviewOpen(true)}
                                startIcon={<VisibilityIcon fontSize="small" />}
                            >
                                Preview diagram
                            </Button>
                        </Box>
                    }
                />
            </Box>

            <Box className={classes.table}>
                <Box className={classes.headerRow}>
                    <span />
                    <span />
                    <span>Task</span>
                    <span>Responsibility</span>
                    <span>Priority</span>
                    <span>Due date</span>
                    <span />
                </Box>

                <Box className={classes.taskListScroll}>
                    <Box
                        className={classes.taskListContainer}
                    >
                        <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId="taskflow-task-list">
                                {(droppableProvided) => (
                                    <div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                                        {activeTaskflow.taskOrder.map((taskId, index) => {
                                            const task = activeTaskflow.tasks[taskId];
                                            if (!task) {
                                                return null;
                                            }
                                            return (
                                                <Draggable key={task.id} draggableId={task.id} index={index}>
                                                    {(draggableProvided, snapshot) => (
                                                        <div
                                                            ref={draggableProvided.innerRef}
                                                            {...draggableProvided.draggableProps}
                                                            className={`${classes.row} ${snapshot.isDragging ? classes.rowDragging : ''}`}
                                                        >
                                                            <span {...draggableProvided.dragHandleProps} className={classes.grip}>
                                                                <DragIndicatorIcon fontSize="small" />
                                                            </span>
                                                            <span className={`${classes.stepNumber} ${task.isGate ? classes.stepNumberGate : ''}`}>
                                                                {index + 1}
                                                            </span>
                                                            <Typography className={classes.desc}>{task.title}</Typography>
                                                            <span className={classes.resp}>
                                                                <span className={classes.laneDot} style={{ backgroundColor: LANE_COLOR[task.lane] }} />
                                                                {TASKFLOW_LANE_LABEL[task.lane]}
                                                            </span>
                                                            <span className={classes.priority}>
                                                                {task.priority === 'low' ? 'Low' : task.priority === 'med' ? 'Medium' : 'High'}
                                                            </span>
                                                            <span className={classes.due}>{describeDueDate(task)}</span>
                                                            <Box className={classes.rowActions}>
                                                                <IconButton size="small" className={classes.editIconBtn} onClick={openTaskEditor(task.id)}>
                                                                    <EditOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton size="small" className={classes.deleteIconBtn} onClick={requestDeleteTask(task)}>
                                                                    <DeleteOutlineIcon fontSize="small" />
                                                                </IconButton>
                                                            </Box>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                        {droppableProvided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </Box>
                </Box>
            </Box>

            <DiagramPreviewDialog
                open={isDiagramPreviewOpen}
                onClose={() => setIsDiagramPreviewOpen(false)}
                onAddTask={addTask}
                onEditTask={(taskId) => openTaskEditor(taskId)()}
            />

            <ConfigureTaskDialog
                open={isConfigureTaskDialogOpen}
                draftTask={draftTask}
                onClose={closeConfigureTaskDialog}
            />

            <DeleteConfirmationDialog
                open={!!taskPendingDelete}
                onClose={closeDeleteConfirmation}
                onConfirm={confirmDeleteTask}
                itemType="task"
                itemName={taskPendingDelete?.title}
            />
        </Box>
    );
};
